import { apiFetch } from './apiClient';

export const MARKETING_ATTRIBUTION_STORAGE_KEY = 'black_peak_marketing_attribution';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const PRODUCTION_HOSTNAMES = new Set(['blackpeakanalytics.com', 'www.blackpeakanalytics.com']);
const UTM_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];

function storage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
}

function createAnonymousVisitorId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  if (window.crypto?.getRandomValues) {
    const values = new Uint8Array(16);
    window.crypto.getRandomValues(values);
    return Array.from(values, value => value.toString(16).padStart(2, '0')).join('');
  }
  return `visitor-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function cleanValue(value, maxLength = 255) {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().replace(/\s+/g, ' ');
  return normalized ? normalized.slice(0, maxLength) : null;
}

function sanitizeReferrer(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    // Query strings and paths can contain identifiers, so persist only the
    // referring origin for anonymous visitors.
    return new URL(value).origin.slice(0, 2048);
  } catch (error) {
    return null;
  }
}

export function isProductionMarketingEnvironment() {
  if (process.env.NODE_ENV !== 'production' || typeof window === 'undefined') return false;
  return PRODUCTION_HOSTNAMES.has(window.location.hostname.toLowerCase());
}

export function readStoredMarketingAttribution() {
  const currentStorage = storage();
  if (!currentStorage) return null;
  try {
    const parsed = JSON.parse(currentStorage.getItem(MARKETING_ATTRIBUTION_STORAGE_KEY) || 'null');
    return parsed?.anonymousVisitorId ? parsed : null;
  } catch (error) {
    return null;
  }
}

export function parseMarketingAttribution(search, { referrer = '', landingPath = '/' } = {}) {
  const params = new URLSearchParams(search || '');
  return {
    utmSource: cleanValue(params.get('utm_source')),
    utmMedium: cleanValue(params.get('utm_medium')),
    utmCampaign: cleanValue(params.get('utm_campaign')),
    utmContent: cleanValue(params.get('utm_content')),
    referrer: sanitizeReferrer(referrer),
    landingPath: cleanValue(landingPath, 2048) || '/',
  };
}

export function getOrCreateMarketingAttribution({ search = '', referrer = '', landingPath = '/' } = {}) {
  const currentStorage = storage();
  const existing = readStoredMarketingAttribution();
  if (existing) return existing;
  const parsed = parseMarketingAttribution(search, { referrer, landingPath });
  const attribution = { anonymousVisitorId: createAnonymousVisitorId(), ...parsed };
  try {
    currentStorage?.setItem(MARKETING_ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  } catch (error) {
    // Tracking is optional and must never block the public page.
  }
  return attribution;
}

export function getMarketingVisitorId() {
  return readStoredMarketingAttribution()?.anonymousVisitorId || null;
}

export async function captureMarketingVisit({ search = '', referrer = '', landingPath = '/' } = {}) {
  if (!isProductionMarketingEnvironment()) return { created: false, skipped: true };
  const attribution = getOrCreateMarketingAttribution({ search, referrer, landingPath });
  const currentStorage = storage();
  const recordedKey = `${MARKETING_ATTRIBUTION_STORAGE_KEY}:recorded`;
  if (currentStorage?.getItem(recordedKey) === attribution.anonymousVisitorId) {
    return { created: false, deduplicated: true };
  }

  const response = await apiFetch(`${API_BASE_URL}/marketing/visits`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      anonymous_visitor_id: attribution.anonymousVisitorId,
      utm_source: attribution.utmSource,
      utm_medium: attribution.utmMedium,
      utm_campaign: attribution.utmCampaign,
      utm_content: attribution.utmContent,
      referrer: attribution.referrer,
      landing_path: attribution.landingPath,
    }),
  });
  const result = await response.json().catch(() => ({ created: false }));
  if (response.ok && result.status === 'recorded') {
    try {
      currentStorage?.setItem(recordedKey, attribution.anonymousVisitorId);
    } catch (error) {
      // The server-side unique constraint remains the final deduplication guard.
    }
  }
  return result;
}

export function buildMarketingSignupField() {
  const visitorId = getMarketingVisitorId();
  return visitorId ? { anonymous_visitor_id: visitorId } : {};
}

export function marketingUtmFields() {
  return UTM_FIELDS;
}
